import { T } from '@design/index';
import type { LabResultGroup, LabResultParameter } from '../types';

/**
 * What the lab actually measured.
 *
 * `GET /lab/reports/:id` has carried this from the day it was written and no
 * screen in either app rendered a single value — so a patient was told a result
 * was critical and could not see which one, what it read, or how far outside
 * the range it was.
 *
 * Every column here is the lab's: the value, the unit, the range it compares
 * against and the flag it set. Nothing is re-derived — a value is never
 * compared against its own range to decide a colour, because the lab has
 * already made that judgement and only the lab is qualified to.
 */

const FLAG: Record<string, { label: string; fg: string; bg: string }> = {
  NORMAL: { label: 'Normal', fg: '#047857', bg: '#ECFDF5' },
  HIGH: { label: 'High', fg: '#B45309', bg: '#FFFBEB' },
  LOW: { label: 'Low', fg: '#B45309', bg: '#FFFBEB' },
  CRITICAL_HIGH: { label: 'Critically high', fg: '#B91C1C', bg: '#FEF2F2' },
  CRITICAL_LOW: { label: 'Critically low', fg: '#B91C1C', bg: '#FEF2F2' },
  ABNORMAL: { label: 'Abnormal', fg: '#B45309', bg: '#FFFBEB' },
};

const flagOf = (p: LabResultParameter) =>
  FLAG[p.flag] ?? { label: p.flag.replace(/_/g, ' ').toLowerCase(), fg: T.textSec, bg: T.ice };

/** One report rendered as plain text, for sharing or saving. */
export function resultsAsText(opts: {
  testName: string;
  labName: string;
  reportNumber?: string;
  reportDate?: string;
  results?: LabResultGroup[];
}): string {
  const lines: string[] = [
    opts.testName,
    [opts.labName, opts.reportNumber, opts.reportDate].filter(Boolean).join(' · '),
    '',
  ];
  for (const g of opts.results ?? []) {
    lines.push(g.testName);
    for (const p of g.parameters) {
      lines.push(
        `  ${[p.name, [p.value, p.unit].filter(Boolean).join(' ')].filter(Boolean).join(': ')}` +
        `${p.refRange ? `  (ref ${p.refRange})` : ''}` +
        `${p.flag && p.flag !== 'NORMAL' ? `  [${flagOf(p).label}]` : ''}`,
      );
    }
    lines.push('');
  }
  lines.push('Issued by the lab and shown in NeoHealthCard. Discuss any result with your doctor.');
  return lines.join('\n');
}

export default function LabResultTable({ results }: { readonly results?: LabResultGroup[] }) {
  if (!results || results.length === 0) {
    return (
      <p style={{ fontSize: 11.5, color: T.textSec, fontFamily: T.font, lineHeight: 1.6 }}>
        The lab released this report without a parameter breakdown, so there are no values to show here.
        Ask the lab for the printed report.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {results.map((g) => (
        <div key={g.testName}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: T.text, fontFamily: T.font, marginBottom: 6 }}>
            {g.testName}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {g.parameters.map((p) => {
              const f = flagOf(p);
              return (
                <div
                  key={`${g.testName}-${p.name ?? ''}-${p.value ?? ''}`}
                  style={{
                    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                    padding: '7px 10px', borderRadius: 10,
                    background: p.isCritical ? '#FEF2F2' : T.ice,
                    border: `1px solid ${p.isCritical ? '#FECACA' : T.border}`,
                  }}
                >
                  <span style={{ fontSize: 11.5, color: T.text, fontFamily: T.font, flex: 1, minWidth: 120 }}>
                    {p.name ?? 'Parameter'}
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: p.isCritical ? '#B91C1C' : T.text, fontFamily: T.font }}>
                    {[p.value, p.unit].filter(Boolean).join(' ') || '—'}
                  </span>
                  {/* What the lab compares against. Absent when the lab sent
                      none — a range is never invented to fill the column. */}
                  {p.refRange && (
                    <span style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, whiteSpace: 'nowrap' }}>
                      ref {p.refRange}
                    </span>
                  )}
                  <span style={{
                    fontSize: 9.5, fontWeight: 700, fontFamily: T.font, whiteSpace: 'nowrap',
                    color: f.fg, background: f.bg, borderRadius: 6, padding: '2px 7px',
                  }}>
                    {f.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <p style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, lineHeight: 1.6 }}>
        Values, ranges and flags are the lab&rsquo;s own. Discuss any result with your doctor — this app does not
        interpret them.
      </p>
    </div>
  );
}
